import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, Input, Optional, Output } from "@angular/core";
import { CsRadioBase } from "./cs-radio-component-interface";
import { CsRadioGroupComponent } from "./cs-radio-group-component";
import { RadioGroupDirective } from "directives/radio-group/cs-radio-group-directive";
import { syncButtonNameFromHost } from "directives/cs-capture-cover/host-name-source";

import { LL } from 'i18n/i18n.service';
@Component({
    selector: 'cs-radio',
    templateUrl: 'cs-radio-component.html',
    host: { 'class': 'radio' },
    providers: [{ provide: CsRadioBase, useExisting: CsRadioComponent }],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsRadioComponent extends CsRadioBase {
  protected LL = LL;

  // attribute "no-border"
  @Input('no-border') noBorder = undefined;
  @Input('icon') icon = undefined;
  @Input() value: any;
  @Input() disabled: boolean = false;
  @Input() recommended: boolean = false;
  @Input() default: boolean = false;
  @Output() select: EventEmitter<void> = new EventEmitter();
  @Output() disabledChange: EventEmitter<boolean> = new EventEmitter();
  @ContentChild('[custom-content]', { static: true }) customContentRef: ElementRef;

  checked: boolean = false;

  get hasCustomContent(): boolean {
    return !!this.customContentRef;
  }

  constructor(
    public elementRef: ElementRef,
    @Optional() private groupComponent: CsRadioGroupComponent,
    @Optional() private groupDirective: RadioGroupDirective,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  group: CsRadioGroupComponent | RadioGroupDirective;

  ngOnInit() {
    this.group = this.groupComponent || this.groupDirective;
    if (this.group && this.value === this.group.selected) {
      this.refreshSelection();
    }
    this.updateDisabledState();
  }

  ngOnChanges() {
    this.updateDisabledState();
  }

  ngAfterViewChecked() {
    // the inner <button> is empty, so it takes its name from the option's drawn text
    syncButtonNameFromHost(this.elementRef, undefined);
  }

  private updateDisabledState() {
    const element = this.elementRef.nativeElement as HTMLElement;
    if (this.disabled) {
      element.classList.add('radio-disabled');
    } else {
      element.classList.remove('radio-disabled');
    }
  }

  refreshSelection() {
    if (this.group && this.value === this.group.selected) {
      this.checked = true;
      let element = this.elementRef.nativeElement as HTMLElement;
      element.classList.add('radio-checked');
    } else {
      this.checked = false;
      let element = this.elementRef.nativeElement as HTMLElement;
      element.classList.remove('radio-checked');
    }

    this.cdr.markForCheck();
  }

  deselect() {
    this.checked = false;
    let element = this.elementRef.nativeElement as HTMLElement;
    element.classList.remove('radio-checked');
  }

  handleButtonClick(ev: Event) {
    void ev;
    if (this.disabled) {
      return; // Don't do anything if disabled
    }
    if (!this.checked) {
      this.group.deselectAll();
      this.checked = true;
      this.group.select(this.value);
      this.select.emit();
      let element = this.elementRef.nativeElement as HTMLElement;
      element.classList.add('radio-checked');
      this.cdr.markForCheck();
    } else {
      // Also emit select event when clicking already selected radio
      // This allows popovers to close even when keeping the same selection
      this.select.emit();
    }
  }
}
